import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ChevronLeft, Coffee, Package, Printer, Calculator, ClipboardList,
    Plus, Trash2, Lock, RefreshCw, IndianRupee,
} from "lucide-react";

import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
    { key: "menu",   label: "Menu & Prices", icon: ClipboardList, role_all: true,  financial: false },
    { key: "sales",  label: "Daily Sales",   icon: Coffee,         role_all: true, financial: false },
    { key: "snacks", label: "Snack Counter", icon: Package,        role_all: true, financial: false },
    { key: "print",  label: "Print / Xerox", icon: Printer,        role_all: true, financial: false },
    { key: "recon",  label: "Reconciliation",icon: Calculator,     role_all: false, financial: true },  // hidden for counter_staff AND non-financial
];

const fmt = (n) => (n === undefined || n === null) ? "" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function CoworkingPage() {
    const { siteId } = useParams();
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const isCounterOnly = !!(user?.capabilities?.counter_staff) && user.role === "SITE_MANAGER";
    const hasFinancial = user?.effective_role === "OWNER" || user?.effective_role === "HEAD";
    const visibleTabs = TABS.filter((t) => {
        if (t.financial && !hasFinancial) return false;
        if (!t.role_all && isCounterOnly) return false;
        return true;
    });
    const tab = params.get("tab") || "sales";
    const [site, setSite] = useState(null);
    useEffect(() => {
        api.get(`/sites/${siteId}`).then(({ data }) => setSite(data)).catch((e) => toast.error(apiError(e)));
    }, [siteId]);
    if (!site) return <div className="mx-auto max-w-6xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;
    const setTab = (t) => setParams({ tab: t }, { replace: true });
    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355] mb-3">
                <ChevronLeft className="w-4 h-4" /> Sites
            </Link>
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Coworking sales &amp; cash</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                    <Coffee className="w-6 h-6" /> {site.name}
                </h1>
                <div className="text-xs text-gray-500 mt-1">{site.address}</div>
            </div>
            <div className="mt-6 flex gap-1 border-b border-gray-200 overflow-x-auto">
                {visibleTabs.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#1E3355] text-[#1E3355]" : "border-transparent text-gray-500 hover:text-[#1E3355]"}`}
                            data-testid={`tab-${t.key}`}
                        >
                            <Icon className="w-4 h-4" /> {t.label}
                        </button>
                    );
                })}
            </div>
            <div className="mt-6">
                {tab === "menu"   && <MenuTab siteId={siteId} />}
                {tab === "sales"  && <SalesTab siteId={siteId} />}
                {tab === "snacks" && <SnacksTab siteId={siteId} />}
                {tab === "print"  && <PrintTab siteId={siteId} />}
                {tab === "recon"  && hasFinancial && !isCounterOnly && <ReconTab siteId={siteId} />}
            </div>
        </div>
    );
}

// ---------- MENU TAB ----------
function MenuTab({ siteId }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ category: "TEA_COFFEE", name: "", initial_price: "", has_staff_discount: false, effective_from: new Date().toISOString().slice(0, 10) });

    const load = useCallback(() => api.get("/coworking/menu").then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e))), []);
    useEffect(() => { load(); }, [load]);

    async function addItem(e) {
        e.preventDefault();
        try {
            await api.post("/coworking/menu", {
                category: form.category, name: form.name.trim(),
                has_staff_discount: form.has_staff_discount,
                initial_price: parseFloat(form.initial_price),
                effective_from: form.effective_from,
            });
            setForm({ ...form, name: "", initial_price: "" });
            setShowAdd(false); toast.success("Item added"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function changePrice(item) {
        const p = window.prompt(`New price for ${item.name} (from date):`, item.price_today);
        if (!p) return;
        const d = window.prompt("Effective from (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
        if (!d) return;
        try {
            await api.post(`/coworking/menu/${item.id}/prices`, { price: parseFloat(p), effective_from: d });
            toast.success("Price scheduled"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    const byCat = { TEA_COFFEE: [], LIVE_KITCHEN: [] };
    items.forEach((i) => (byCat[i.category] || byCat.TEA_COFFEE).push(i));
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-600">{items.length} items · prices are effective-dated (never retroactive).</div>
                {user?.role === "HO_ADMIN" && (
                    <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 bg-[#1E3355] text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="add-menu-item"><Plus className="w-4 h-4" /> Add item</button>
                )}
            </div>
            {showAdd && (
                <form onSubmit={addItem} className="mb-4 rounded-md bg-white border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={FIELD}>
                        <option value="TEA_COFFEE">Tea/Coffee</option>
                        <option value="LIVE_KITCHEN">Live Kitchen</option>
                    </select>
                    <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={FIELD} />
                    <input type="number" required placeholder="Price ₹" value={form.initial_price} onChange={(e) => setForm({ ...form, initial_price: e.target.value })} className={FIELD} />
                    <input type="date" required value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className={FIELD} />
                    <div className="flex gap-2">
                        <button type="submit" className="flex-1 bg-[#1E3355] text-white text-sm rounded-md">Add</button>
                        <button type="button" onClick={() => setShowAdd(false)} className="px-3 text-sm text-gray-600">Cancel</button>
                    </div>
                </form>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[["TEA_COFFEE", "Tea / Coffee"], ["LIVE_KITCHEN", "Live Kitchen"]].map(([k, label]) => (
                    <div key={k} className="rounded-xl bg-white border border-gray-100 fx-shadow-card">
                        <div className="p-3 border-b border-gray-100 bg-[#F4F6F9] font-display font-bold text-sm text-[#1E3355]">{label} · {byCat[k].length}</div>
                        <table className="w-full text-sm">
                            <tbody>
                                {byCat[k].map((i) => (
                                    <tr key={i.id} className="border-t border-gray-100">
                                        <td className="p-2">
                                            <div className="font-medium text-[#1E3355]" data-testid={`menu-name-${i.name}`}>{i.name}{i.has_staff_discount && <span className="ml-2 text-[10px] font-normal text-emerald-700 bg-emerald-50 px-1 rounded">STAFF</span>}</div>
                                            <div className="text-[10px] text-gray-500">{(i.price_history || []).length} price(s) on file</div>
                                        </td>
                                        <td className="p-2 text-right font-semibold" data-testid={`menu-price-${i.name}`}>₹{fmt(i.price_today)}</td>
                                        {user?.role === "HO_ADMIN" && (
                                            <td className="p-2 text-right"><button onClick={() => changePrice(i)} className="text-xs text-[#1E3355] hover:underline" data-testid={`change-price-${i.name}`}>Change</button></td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- SALES TAB ----------
function SalesTab({ siteId }) {
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [items, setItems] = useState([]);
    const [sales, setSales] = useState([]);
    const [counts, setCounts] = useState({}); // item_id -> {staff, visitor}

    const loadItems = () => api.get(`/coworking/menu?date=${date}`).then(({ data }) => setItems(data));
    const loadSales = () => api.get(`/coworking/${siteId}/sales?month=${month}`).then(({ data }) => setSales(data));
    useEffect(() => { loadItems(); }, [date]);
    useEffect(() => { loadSales(); }, [month]);

    async function addSale(item) {
        const c = counts[item.id] || { staff: 0, visitor: 0 };
        if (c.staff + c.visitor <= 0) return;
        try {
            await api.post(`/coworking/${siteId}/sales`, { date, item_id: item.id, staff_qty: c.staff, visitor_qty: c.visitor });
            setCounts({ ...counts, [item.id]: { staff: 0, visitor: 0 } });
            loadSales();
            toast.success(`Recorded ${item.name}`);
        } catch (e) { toast.error(apiError(e)); }
    }
    async function delSale(sid) {
        if (!window.confirm("Remove this sale?")) return;
        try { await api.delete(`/coworking/${siteId}/sales/${sid}`); loadSales(); }
        catch (e) { toast.error(apiError(e)); }
    }
    const totalToday = sales.filter((s) => s.date === date).reduce((a, s) => a + s.total_amount, 0);
    const totalMonth = sales.reduce((a, s) => a + s.total_amount, 0);
    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={FIELD + " w-40"} data-testid="sales-date" />
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD + " w-40"} data-testid="sales-month" />
                <div className="ml-auto text-sm">Today: <span className="font-semibold text-[#1E3355]">₹{fmt(totalToday)}</span> · Month: <span className="font-semibold text-[#1E3355]">₹{fmt(totalMonth)}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider mb-2">Enter sales</h3>
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card divide-y divide-gray-100">
                        {items.map((i) => {
                            const c = counts[i.id] || { staff: 0, visitor: 0 };
                            const setC = (k, v) => setCounts({ ...counts, [i.id]: { ...c, [k]: Math.max(0, parseInt(v || "0")) } });
                            return (
                                <div key={i.id} className="p-3 flex items-center gap-2" data-testid={`sale-row-${i.name}`}>
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-[#1E3355]">{i.name}</div>
                                        <div className="text-[10px] text-gray-500">₹{fmt(i.price_today)} · {i.category === "TEA_COFFEE" ? "Tea/Coffee" : "Live Kitchen"}</div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <label className="text-[10px] text-gray-500">Staff</label>
                                        <QtyInput value={c.staff} onChange={(v) => setC("staff", v)} testid={`staff-qty-${i.name}`} />
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <label className="text-[10px] text-gray-500">Visitor</label>
                                        <QtyInput value={c.visitor} onChange={(v) => setC("visitor", v)} testid={`visitor-qty-${i.name}`} />
                                    </div>
                                    <button
                                        onClick={() => addSale(i)}
                                        disabled={c.staff + c.visitor <= 0}
                                        className="bg-[#1E3355] text-white text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-40"
                                        data-testid={`add-sale-${i.name}`}
                                    >Add</button>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider mb-2">Recorded — {month}</h3>
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card max-h-[70vh] overflow-y-auto">
                        <table className="w-full text-sm">
                            <tbody>
                                {sales.map((s) => (
                                    <tr key={s.id} className="border-b border-gray-100">
                                        <td className="p-2 text-xs text-gray-500 w-20">{fmtDate(s.date)}</td>
                                        <td className="p-2">
                                            <div className="font-medium text-sm">{s.item_name}</div>
                                            <div className="text-[10px] text-gray-500">S:{s.staff_qty} · V:{s.visitor_qty}</div>
                                        </td>
                                        <td className="p-2 text-right font-semibold">₹{fmt(s.total_amount)}</td>
                                        <td className="p-1"><button onClick={() => delSale(s.id)} className="text-[#E02B2B] p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                                    </tr>
                                ))}
                                {sales.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-gray-400 italic text-xs">No sales recorded this month.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function QtyInput({ value, onChange, testid }) {
    return (
        <div className="flex items-center gap-1">
            <button onClick={() => onChange(String(Math.max(0, (value || 0) - 1)))} className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-[#1E3355] text-sm">−</button>
            <input value={value || 0} onChange={(e) => onChange(e.target.value)} className="w-10 text-center text-sm border border-gray-200 rounded" data-testid={testid} />
            <button onClick={() => onChange(String((value || 0) + 1))} className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-[#1E3355] text-sm">+</button>
        </div>
    );
}

// ---------- SNACKS TAB ----------
function SnacksTab({ siteId }) {
    const { user } = useAuth();
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [products, setProducts] = useState([]);
    const [stock, setStock] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [saleForm, setSaleForm] = useState({});  // pid -> count
    const [pForm, setPForm] = useState({ date: new Date().toISOString().slice(0, 10), product_id: "", qty: "", unit: "pcs", net_amount: "", discount: "0", gst_pct: "12" });

    const load = useCallback(async () => {
        try {
            const [p, s, pu] = await Promise.all([
                api.get("/coworking/snacks/products"),
                api.get(`/coworking/${siteId}/snacks/stock?month=${month}`),
                api.get(`/coworking/${siteId}/snacks/purchases?month=${month}`),
            ]);
            setProducts(p.data); setStock(s.data); setPurchases(pu.data);
            if (!pForm.product_id && p.data.length) setPForm((f) => ({ ...f, product_id: p.data[0].id }));
        } catch (e) { toast.error(apiError(e)); }
    }, [siteId, month]);
    useEffect(() => { load(); }, [load]);

    async function addSale(pid) {
        const count = parseInt(saleForm[pid] || "0");
        if (count <= 0) return;
        try {
            await api.post(`/coworking/${siteId}/snacks/sales`, { date: new Date().toISOString().slice(0, 10), product_id: pid, count });
            setSaleForm({ ...saleForm, [pid]: 0 }); toast.success("Sale recorded"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function addPurchase(e) {
        e.preventDefault();
        try {
            await api.post(`/coworking/${siteId}/snacks/purchases`, {
                date: pForm.date, product_id: pForm.product_id,
                qty: parseFloat(pForm.qty), unit: pForm.unit,
                net_amount: parseFloat(pForm.net_amount),
                discount: parseFloat(pForm.discount || "0"),
                gst_pct: parseFloat(pForm.gst_pct || "0"),
            });
            setPForm({ ...pForm, qty: "", net_amount: "" });
            toast.success("Purchase recorded"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    const totalMargin = stock.reduce((a, s) => a + (s.sales_amount_month - s.purchase_cost_total), 0);
    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD + " w-40"} data-testid="snack-month" />
                <div className="ml-auto text-sm">Sales-Purchases margin (life-to-date): <span className={`font-semibold ${totalMargin >= 0 ? "text-emerald-700" : "text-[#E02B2B]"}`}>₹{fmt(totalMargin)}</span></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider mb-2">Products · stock &amp; sales</h3>
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F4F6F9]">
                                <tr>
                                    <th className="text-left p-2">Product</th>
                                    <th className="text-right p-2">Price</th>
                                    <th className="text-right p-2">Stock</th>
                                    <th className="text-right p-2">{month} sales</th>
                                    <th className="text-right p-2">Sell</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stock.map((row) => (
                                    <tr key={row.product.id} className={`border-t border-gray-100 ${row.low_stock ? "bg-amber-50" : ""}`}>
                                        <td className="p-2">
                                            <div className="font-medium text-[#1E3355]" data-testid={`snack-name-${row.product.name}`}>{row.product.name}</div>
                                            <div className="text-[10px] text-gray-500">{row.product.product_type}{row.low_stock ? " · LOW STOCK" : ""}</div>
                                        </td>
                                        <td className="p-2 text-right">₹{fmt(row.product.selling_price)}</td>
                                        <td className="p-2 text-right font-semibold">{row.stock_on_hand}</td>
                                        <td className="p-2 text-right">{row.sales_qty_month} · ₹{fmt(row.sales_amount_month)}</td>
                                        <td className="p-2 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <input type="number" min="0" value={saleForm[row.product.id] || 0} onChange={(e) => setSaleForm({ ...saleForm, [row.product.id]: e.target.value })} className="w-12 text-center border border-gray-200 rounded text-xs" data-testid={`sell-qty-${row.product.name}`} />
                                                <button onClick={() => addSale(row.product.id)} className="bg-[#1E3355] text-white text-[11px] font-semibold px-2 py-1 rounded" data-testid={`sell-btn-${row.product.name}`}>Sell</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div>
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider mb-2">New purchase</h3>
                    <form onSubmit={addPurchase} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 space-y-2">
                        <input type="date" required value={pForm.date} onChange={(e) => setPForm({ ...pForm, date: e.target.value })} className={FIELD} />
                        <select value={pForm.product_id} onChange={(e) => setPForm({ ...pForm, product_id: e.target.value })} className={FIELD}>
                            {products.map((p) => <option key={p.id} value={p.id}>{p.product_type} · {p.name}</option>)}
                        </select>
                        <div className="grid grid-cols-2 gap-2">
                            <input type="number" required placeholder="Qty" value={pForm.qty} onChange={(e) => setPForm({ ...pForm, qty: e.target.value })} className={FIELD} data-testid="purchase-qty" />
                            <input placeholder="Unit (pcs)" value={pForm.unit} onChange={(e) => setPForm({ ...pForm, unit: e.target.value })} className={FIELD} />
                        </div>
                        <input type="number" required placeholder="Net amount ₹" value={pForm.net_amount} onChange={(e) => setPForm({ ...pForm, net_amount: e.target.value })} className={FIELD} data-testid="purchase-net" />
                        <div className="grid grid-cols-2 gap-2">
                            <input type="number" placeholder="Discount ₹" value={pForm.discount} onChange={(e) => setPForm({ ...pForm, discount: e.target.value })} className={FIELD} />
                            <input type="number" placeholder="GST %" value={pForm.gst_pct} onChange={(e) => setPForm({ ...pForm, gst_pct: e.target.value })} className={FIELD} />
                        </div>
                        <button type="submit" className="w-full bg-[#1E3355] text-white text-sm py-2 rounded-md" data-testid="save-purchase-btn">Record purchase</button>
                    </form>
                    {purchases.length > 0 && (
                        <div className="mt-3">
                            <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">{month} purchases</div>
                            <ul className="space-y-1 text-xs">
                                {purchases.slice(0, 10).map((p) => (
                                    <li key={p.id} className="flex justify-between border-b border-gray-100 py-1">
                                        <span>{fmtDate(p.date)} · {p.product_name} × {p.qty}</span>
                                        <span className="font-semibold">₹{fmt(p.total_amount)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------- PRINT TAB ----------
function PrintTab({ siteId }) {
    const { user } = useAuth();
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [logs, setLogs] = useState([]);
    const [rates, setRates] = useState([]);
    const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), kind: "PRINT", count: "", is_staff: false });

    const load = useCallback(async () => {
        try {
            const [l, r] = await Promise.all([api.get(`/coworking/${siteId}/print?month=${month}`), api.get("/coworking/print/rates")]);
            setLogs(l.data); setRates(r.data);
        } catch (e) { toast.error(apiError(e)); }
    }, [siteId, month]);
    useEffect(() => { load(); }, [load]);

    async function addRate() {
        const kind = window.prompt("Kind (PRINT or XEROX):", "PRINT");
        if (!kind) return;
        const rate = window.prompt("Rate ₹/page:", "10");
        if (!rate) return;
        const eff = window.prompt("Effective from (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
        if (!eff) return;
        try {
            await api.post("/coworking/print/rates", { kind: kind.toUpperCase(), rate: parseFloat(rate), effective_from: eff });
            toast.success("Rate added"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function addLog(e) {
        e.preventDefault();
        try {
            await api.post(`/coworking/${siteId}/print`, { date: form.date, kind: form.kind, count: parseInt(form.count), is_staff: form.is_staff });
            setForm({ ...form, count: "" }); toast.success("Log added"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    const total = logs.reduce((a, l) => a + l.amount, 0);
    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD + " w-40"} data-testid="print-month" />
                <div className="ml-auto text-sm">Month total <span className="font-semibold text-[#1E3355]">₹{fmt(total)}</span></div>
                {user?.role === "HO_ADMIN" && <button onClick={addRate} className="text-xs text-[#1E3355] hover:underline" data-testid="add-rate-btn">+ Rate change</button>}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider mb-2">Add entry</h3>
                    <form onSubmit={addLog} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 space-y-2">
                        <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={FIELD} />
                        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={FIELD}>
                            <option value="PRINT">Print</option>
                            <option value="XEROX">Xerox</option>
                        </select>
                        <input type="number" required placeholder="Number of copies" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className={FIELD} data-testid="print-count" />
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.is_staff} onChange={(e) => setForm({ ...form, is_staff: e.target.checked })} /> Staff
                        </label>
                        <button type="submit" className="w-full bg-[#1E3355] text-white text-sm py-2 rounded-md" data-testid="save-print-btn">Add log</button>
                    </form>
                    <div className="mt-3 text-xs">
                        <div className="font-bold uppercase tracking-widest text-gray-500 mb-1">Current rates</div>
                        {rates.slice(0, 5).map((r) => (
                            <div key={r.id} className="flex justify-between border-b border-gray-100 py-1">
                                <span>{r.kind} · from {r.effective_from}</span>
                                <span className="font-semibold">₹{fmt(r.rate)}/page</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider mb-2">{month} log</h3>
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card">
                        <table className="w-full text-sm">
                            <tbody>
                                {logs.map((l) => (
                                    <tr key={l.id} className="border-b border-gray-100">
                                        <td className="p-2 text-xs text-gray-500 w-20">{fmtDate(l.date)}</td>
                                        <td className="p-2">
                                            <div className="font-medium">{l.kind} · {l.count} pages</div>
                                            <div className="text-[10px] text-gray-500">{l.is_staff ? "Staff" : "Visitor"} · ₹{l.unit_rate_snapshot}/page</div>
                                        </td>
                                        <td className="p-2 text-right font-semibold">₹{fmt(l.amount)}</td>
                                    </tr>
                                ))}
                                {logs.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-gray-400 italic text-xs">No logs this month.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------- RECON TAB ----------
function ReconTab({ siteId }) {
    const { user } = useAuth();
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [showHandover, setShowHandover] = useState(false);
    const [hForm, setHForm] = useState({ on_date: new Date().toISOString().slice(0, 10), amount: "", to_person: "", note: "" });

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/coworking/${siteId}/reconciliation?month=${month}`); setData(data); }
        catch (e) { toast.error(apiError(e)); }
    }, [siteId, month]);
    useEffect(() => { load(); }, [load]);

    async function addHandover(e) {
        e.preventDefault();
        try {
            await api.post(`/coworking/${siteId}/reconciliation/handovers`, {
                on_date: hForm.on_date, amount: parseFloat(hForm.amount),
                to_person: hForm.to_person, note: hForm.note,
            });
            setHForm({ ...hForm, amount: "", to_person: "", note: "" });
            setShowHandover(false); toast.success("Handover recorded"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function finalise() {
        if (!window.confirm(`Finalise ${month}? This locks sales + recon irreversibly (HO can unlock later via Expenses).`)) return;
        try {
            await api.post(`/coworking/${siteId}/reconciliation/finalise?month=${month}`);
            toast.success("Finalised"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    if (!data) return <div className="h-64 rounded-xl bg-white animate-pulse" />;
    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD + " w-40"} data-testid="recon-month" />
                {data.locked && <span className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded"><Lock className="w-3 h-3" /> Finalised</span>}
                {!data.locked && user?.role === "HO_ADMIN" && (
                    <button onClick={finalise} className="ml-auto inline-flex items-center gap-1 bg-[#E02B2B] hover:bg-red-700 text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="finalise-btn">
                        <Lock className="w-4 h-4" /> Finalise month
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <ReconCard label="Carried forward" value={data.carried_forward} />
                <ReconCard label="Total revenue" value={data.total_revenue} tone="positive" />
                <ReconCard label="Total handovers" value={data.total_handovers} tone="negative" />
                <ReconCard label="Cash in hand" value={data.cash_in_hand} tone="cash" testid="cash-in-hand" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4">
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider mb-2">Revenue streams</h3>
                    <table className="w-full text-sm">
                        <tbody>
                            <tr className="border-b border-gray-100"><td className="py-1.5">Tea / Coffee</td><td className="py-1.5 text-right font-semibold">₹{fmt(data.streams.tea_coffee)}</td></tr>
                            <tr className="border-b border-gray-100"><td className="py-1.5">Live Kitchen</td><td className="py-1.5 text-right font-semibold">₹{fmt(data.streams.live_kitchen)}</td></tr>
                            <tr className="border-b border-gray-100"><td className="py-1.5">Snacks</td><td className="py-1.5 text-right font-semibold">₹{fmt(data.streams.snacks)}</td></tr>
                            <tr className="border-b border-gray-100"><td className="py-1.5">Print / Xerox</td><td className="py-1.5 text-right font-semibold">₹{fmt(data.streams.printing)}</td></tr>
                            <tr className="bg-[#F4F6F9]"><td className="py-2 font-bold">Total</td><td className="py-2 text-right font-bold">₹{fmt(data.total_revenue)}</td></tr>
                        </tbody>
                    </table>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-wider">Cash handovers</h3>
                        {!data.locked && (
                            <button onClick={() => setShowHandover((s) => !s)} className="text-xs text-[#1E3355] hover:underline" data-testid="add-handover-toggle">+ Add</button>
                        )}
                    </div>
                    {showHandover && (
                        <form onSubmit={addHandover} className="space-y-2 mb-3">
                            <input type="date" required value={hForm.on_date} onChange={(e) => setHForm({ ...hForm, on_date: e.target.value })} className={FIELD} />
                            <input type="number" required placeholder="Amount ₹" value={hForm.amount} onChange={(e) => setHForm({ ...hForm, amount: e.target.value })} className={FIELD} data-testid="handover-amount" />
                            <input required placeholder="To person" value={hForm.to_person} onChange={(e) => setHForm({ ...hForm, to_person: e.target.value })} className={FIELD} data-testid="handover-person" />
                            <input placeholder="Note" value={hForm.note} onChange={(e) => setHForm({ ...hForm, note: e.target.value })} className={FIELD} />
                            <button type="submit" className="w-full bg-[#1E3355] text-white text-sm py-2 rounded-md" data-testid="save-handover-btn">Log handover</button>
                        </form>
                    )}
                    <table className="w-full text-sm">
                        <tbody>
                            {data.handovers.map((h) => (
                                <tr key={h.id} className="border-b border-gray-100">
                                    <td className="py-1.5 text-xs text-gray-500 w-20">{fmtDate(h.on_date)}</td>
                                    <td className="py-1.5">{h.to_person}{h.note ? ` · ${h.note}` : ""}</td>
                                    <td className="py-1.5 text-right font-semibold">₹{fmt(h.amount)}</td>
                                </tr>
                            ))}
                            {data.handovers.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-xs text-gray-400 italic">No handovers this month.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 italic">
                Cash vs UPI split (per-day) can be recorded via <code className="bg-gray-100 px-1 rounded">PATCH /api/coworking/{"{site}"}/reconciliation/daily/{"{date}"}</code>. A discrepancy note is required whenever counted differs from cash + UPI.
            </div>
        </div>
    );
}

function ReconCard({ label, value, tone, testid }) {
    const cls = {
        positive: "bg-emerald-50 border-emerald-200 text-emerald-800",
        negative: "bg-amber-50 border-amber-200 text-amber-800",
        cash:     "bg-[#EEF2F8] border-[#C7D1E2] text-[#1E3355]",
    }[tone] || "bg-white border-gray-200 text-[#1E3355]";
    return (
        <div className={`rounded-xl border p-3 ${cls}`} data-testid={testid}>
            <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</div>
            <div className="font-display text-xl font-bold mt-1">₹{fmt(value)}</div>
        </div>
    );
}

const FIELD = "rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]";
